import React, { useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { CreateGameConfig } from '@par/shared/types/game';
import { Mode } from '../components/room/types';
import { TournamentPlayer, WhoCanJoinGameDialog } from './WhoCanJoinGameDialog';
import { fetchGet } from '../api/common';

type PropTypes = {
  close: () => void;
  onTourneyGameCreate: (gameConfig: CreateGameConfig) => void;
  open: boolean;
};

export const CreateTourneyGameDialog = ({
  close,
  onTourneyGameCreate,
  open,
}: PropTypes) => {
  const theme = useTheme();
  const [customGameName, setCustomGameName] = useState('');
  const [tournament, setTournament] = useState(7);
  const [tournamentQuestionCount, setTournamentQuestionCount] = useState(10);
  const [round, setRound] = useState('finals');
  const [cutoffType, setCutoffType] = useState<'integer' | 'percent'>(
    'integer'
  );
  const [cutoff, setCutoff] = useState(8);
  const [mode, setMode] = useState<Mode>('match');
  const [maxPlayers, setMaxPlayers] = useState(200);
  const [autoStartDuration, setAutoStartDuration] = useState(10);
  const [preventSpectators, setPreventSpectators] = useState(false);
  const [joinGameTimerDuration, setJoinGameTimerDuration] = useState(30);
  const [openWhoCanJoin, setOpenWhoCanJoin] = useState(false);
  const [roundResults, setRoundResults] =
    useState<Record<string, TournamentPlayer[]>>();
  const [whoCanJoin, setWhoCanJoin] = useState<TournamentPlayer[]>();

  useEffect(() => {
    if (!open) return;
    setWhoCanJoin(undefined);
    setRoundResults(undefined);
  }, [open]);

  const createTourneyGame = () => {
    const createGameConfig: CreateGameConfig = {
      allowSpectators: !preventSpectators,
      createEmptyGame: true,
      customGameName: customGameName,
      customSitAndGoMaxPlayers: maxPlayers,
      gameType: 'pro',
      categorySet: 'all',
      mode: mode,
      ranked: false,
      tournament: tournament,
      tournamentQuestionCount: tournamentQuestionCount,
      autoStartDuration: autoStartDuration,
      joinGameTimerDuration: joinGameTimerDuration,
      tournamentRound: round,
      tournamentCutoffType: cutoffType,
      tournamentCutoff: cutoff,
      whoCanJoin: whoCanJoin?.map((p) => p.profileId),
    };
    onTourneyGameCreate(createGameConfig);
    close();
  };

  const handleTournamentChange = (event: SelectChangeEvent) => {
    setTournament(+event.target.value);
  };

  const handleTournamentQuestionCountChange = (event: SelectChangeEvent) => {
    setTournamentQuestionCount(+event.target.value);
  };

  const handleWhoCanJoinClick = async () => {
    const res = await fetchGet(`/tournament/metadata/${tournament}`);
    res.results.participants = res.participants;
    setRoundResults(res.results);
    setOpenWhoCanJoin(true);
  };

  const handleWhoCanJoin = (players: TournamentPlayer[]) => {
    setWhoCanJoin(players);
  };

  return (
    <>
      <WhoCanJoinGameDialog
        close={() => setOpenWhoCanJoin(false)}
        open={openWhoCanJoin}
        roundResults={roundResults}
        whoCanJoin={handleWhoCanJoin}
      ></WhoCanJoinGameDialog>
      <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack spacing={2}>
            <Typography variant="h4" textAlign={'center'}>
              Create Tournament Game
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ width: '100%', height: '100%' }}>
          <Stack direction="row" spacing={2}>
            <Stack spacing={2} width={'50%'} padding={2}>
              <TextField
                id="customGameName"
                label="Game Name"
                variant="standard"
                value={customGameName}
                onChange={(e) => setCustomGameName(e.target.value)}
              />
              <Stack direction="row" spacing={4}>
                <FormControl variant="standard" sx={{ minWidth: 100 }}>
                  <InputLabel>Tournament</InputLabel>
                  <Select
                    value={tournament.toString()}
                    onChange={handleTournamentChange}
                  >
                    <MenuItem value={1}>1</MenuItem>
                    <MenuItem value={2}>2</MenuItem>
                    <MenuItem value={3}>3</MenuItem>
                    <MenuItem value={4}>4</MenuItem>
                    <MenuItem value={5}>5</MenuItem>
                    <MenuItem value={6}>6</MenuItem>
                    <MenuItem value={7}>7</MenuItem>
                  </Select>
                </FormControl>
                <FormControl variant="standard" sx={{ minWidth: 100 }}>
                  <InputLabel>Question Count</InputLabel>
                  <Select
                    value={tournamentQuestionCount.toString()}
                    onChange={handleTournamentQuestionCountChange}
                  >
                    <MenuItem value={3}>3</MenuItem>
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={20}>20</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
              <FormControl sx={{ marginBottom: 3 }}>
                <FormLabel>Mode</FormLabel>
                <RadioGroup
                  row
                  value={mode}
                  onChange={(e) => setMode(e.target.value as Mode)}
                >
                  <FormControlLabel
                    value="match"
                    control={<Radio />}
                    label="Match"
                  />
                  <FormControlLabel
                    value="sit-n-go"
                    control={<Radio />}
                    label="Sit & Go"
                  />
                </RadioGroup>
              </FormControl>
              {mode === 'sit-n-go' ? (
                <TextField
                  id="sitAndGoMaxPlayers"
                  label="Sit & Go Max Players"
                  variant="standard"
                  value={maxPlayers}
                  onChange={(e) => setMaxPlayers(+e.target.value)}
                />
              ) : null}
              <TextField
                id="joinGameTimerDuration"
                label="Join Game Timer Duration"
                variant="standard"
                value={joinGameTimerDuration}
                onChange={(e) => setJoinGameTimerDuration(+e.target.value)}
              />
              <TextField
                id="autoStartDuration"
                label="Auto Start Duration"
                variant="standard"
                value={autoStartDuration}
                onChange={(e) => setAutoStartDuration(+e.target.value)}
              />
              <FormControlLabel
                label="Prevent spectate"
                control={
                  <Checkbox
                    checked={preventSpectators}
                    onChange={(e) => setPreventSpectators(e.target.checked)}
                  />
                }
              />
            </Stack>
            <Stack spacing={2} width={'50%'} padding={2}>
              <FormControl variant="standard" sx={{ minWidth: 100 }}>
                <InputLabel>Round</InputLabel>
                <Select
                  value={round}
                  onChange={(e) => setRound(e.target.value)}
                >
                  <MenuItem value={'finals'}>Finals</MenuItem>
                  <MenuItem value={'semiFinals'}>Semi Finals</MenuItem>
                  <MenuItem value={'cutFour'}>Cut 4</MenuItem>
                  <MenuItem value={'cutThree'}>Cut 3</MenuItem>
                  <MenuItem value={'cutTwo'}>Cut 2</MenuItem>
                  <MenuItem value={'cutOne'}>Cut 1</MenuItem>
                </Select>
              </FormControl>
              <FormControl sx={{ marginBottom: 3 }}>
                <FormLabel>Cutoff Type</FormLabel>
                <RadioGroup
                  row
                  value={cutoffType}
                  onChange={(e) =>
                    setCutoffType(e.target.value as 'integer' | 'percent')
                  }
                >
                  <FormControlLabel
                    value="integer"
                    control={<Radio />}
                    label="Integer"
                  />
                  <FormControlLabel
                    value="percent"
                    control={<Radio />}
                    label="Percent"
                  />
                </RadioGroup>
              </FormControl>
              <TextField
                id="cutoff"
                label="Cutoff (integer)"
                variant="standard"
                value={cutoff}
                onChange={(e) => setCutoff(+e.target.value)}
              />
              <Button variant="outlined" onClick={handleWhoCanJoinClick}>
                Select who can join
              </Button>
              <Stack>
                {whoCanJoin?.map((p) => (
                  <Typography key={p.profileId} variant={'caption'}>
                    - {p.username}
                  </Typography>
                ))}
              </Stack>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={close} fullWidth>
            Cancel
          </Button>
          <Button variant="contained" onClick={createTourneyGame} fullWidth>
            Create Game
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
